import { useState, useEffect } from 'react'
import { useIntelData } from '../../contexts/IntelDataContext'
import { Spinner } from '../../components/Spinner'

const CATEGORIES: string[] = ['', 'economic', 'financial', 'historical', 'statistical', 'policy', 'other']

interface Claim {
  id: number
  influencer_id: number
  influencer_name: string
  claim_text?: string
  text?: string
  category: string
  confidence?: number | string
  entities?: string[] | string
  claim_date?: string
  date?: string
}

interface Influencer {
  id: number
  name: string
}

interface Filters {
  influencer: string
  category: string
}

export default function Claims() {
  const intel = useIntelData()
  const [claims, setClaims] = useState<Claim[]>((intel.claimsCache?.data as Claim[]) || [])
  const [influencers, setInfluencers] = useState<Influencer[]>((intel.influencersCache?.data as Influencer[]) || [])
  const [filters, setFilters] = useState<Filters>({ influencer: '', category: '' })
  const [loading, setLoading] = useState(!intel.claimsCache?.data)

  useEffect(() => {
    if (!intel.isStale(intel.claimsCache?.timestamp)) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true)
    Promise.all([
      fetch('/api/intel/claims').then(r => r.ok ? r.json() as Promise<Claim[]> : Promise.resolve([])).catch(() => [] as Claim[]),
      intel.isStale(intel.influencersCache?.timestamp)
        ? fetch('/api/intel/influencers').then(r => r.ok ? r.json() as Promise<Influencer[]> : Promise.resolve([])).catch(() => [] as Influencer[])
        : Promise.resolve(intel.influencersCache!.data as Influencer[])
    ]).then(([claimsData, influencersData]) => {
      setClaims(claimsData)
      setInfluencers(influencersData)
      intel.cacheClaims(claimsData)
      intel.cacheInfluencers(influencersData)
    }).finally(() => setLoading(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = claims.filter(c => {
    if (filters.influencer && String(c.influencer_id) !== filters.influencer) return false
    if (filters.category && c.category !== filters.category) return false
    return true
  })

  return (
    <div>
      <h2 className="text-xl sm:text-2xl font-bold mb-4 text-gray-900">KOL Claims</h2>

      <div className="flex flex-wrap gap-2 mb-4">
        <select
          value={filters.influencer}
          onChange={e => setFilters(f => ({ ...f, influencer: e.target.value }))}
          className="w-full sm:w-auto bg-white border border-gray-300 rounded px-3 py-1.5 text-sm text-gray-900"
        >
          <option value="">All KOLs</option>
          {influencers.map(i => <option key={i.id} value={String(i.id)}>{i.name}</option>)}
        </select>
        <select
          value={filters.category}
          onChange={e => setFilters(f => ({ ...f, category: e.target.value }))}
          className="w-full sm:w-auto bg-white border border-gray-300 rounded px-3 py-1.5 text-sm text-gray-900"
        >
          {CATEGORIES.map(c => <option key={c} value={c}>{c || 'All categories'}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="bg-white border border-gray-200 rounded-lg h-[400px] flex flex-col items-center justify-center gap-3">
          <Spinner size={32} />
          <span className="text-sm text-gray-500">Loading claims...</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">
          No claims found.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-3 font-semibold text-gray-900">KOL</th>
                <th className="text-left py-3 px-3 font-semibold text-gray-900">Claim</th>
                <th className="text-left py-3 px-3 font-semibold text-gray-900">Category</th>
                <th className="text-left py-3 px-3 font-semibold text-gray-900">Confidence</th>
                <th className="text-left py-3 px-3 font-semibold text-gray-900">Entities</th>
                <th className="text-left py-3 px-3 font-semibold text-gray-900">Date</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="py-3 px-3 font-medium text-gray-900">{c.influencer_name}</td>
                  <td className="py-3 px-3 text-gray-700 max-w-md">{c.claim_text || c.text}</td>
                  <td className="py-3 px-3 text-gray-700 capitalize">{c.category}</td>
                  <td className="py-3 px-3 text-gray-700">
                    {typeof c.confidence === 'number' ? `${Math.round(c.confidence * 100)}%` : c.confidence}
                  </td>
                  <td className="py-3 px-3 text-gray-700">
                    {Array.isArray(c.entities) ? c.entities.join(', ') : c.entities}
                  </td>
                  <td className="py-3 px-3 text-gray-700">{c.claim_date || c.date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
